import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSpreadsheet } from "lucide-react";

const MODES = [["merge", "Merge", "keep their details and only fill in empty cells"], ["override", "Override", "replace their details with the sheet's values"],
  ["new", "Add as new", "always add a new row"]];

export function CourseImportDialog({ tabs, tab, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState(tab);
  const [mode, setMode] = useState("merge");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (commit) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("tab", target); fd.append("commit", commit ? "true" : "false"); fd.append("mode", mode);
    setBusy(true);
    try {
      const { data } = await api.post("/short-term/import", fd);
      if (!commit) { setPreview(data); return; }
      toast.success(`${data.created} new student${data.created === 1 ? "" : "s"}${data.updated ? `, ${data.updated} existing ${mode === "override" ? "overwritten" : "merged"}` : ""} · ${data.tab_label}`);
      (data.warnings || []).slice(0, 3).forEach((w) => toast.message(w));
      onDone(target); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="st-import-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> Import students from Excel</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Upload an .xlsx or .csv file. The first row should be headings — they're matched to your columns (Student name, College, Fee, Paid…). Headings that don't match become new columns.</p>
        <label className="text-xs text-slate-500">Import into
          <select data-testid="st-import-tab" value={target} onChange={(e) => { setTarget(e.target.value); setPreview(null); }} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">{tabs.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select>
        </label>
        <input data-testid="st-import-file" type="file" accept=".xlsx,.xlsm,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} className="text-sm" />
        <div className="space-y-1" data-testid="st-import-mode">
          <div className="text-xs text-slate-500">If a student is already there (same email, phone, or name + college):</div>
          {MODES.map(([k, l, dsc]) => (
            <label key={k} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${mode === k ? "border-primary bg-primary/5" : "border-slate-200"}`}>
              <input type="radio" name="st-mode" data-testid={`st-import-mode-${k}`} checked={mode === k} onChange={() => { setMode(k); setPreview(null); }} className="mt-0.5" />
              <span><b className="text-slate-800">{l}</b> <span className="text-xs text-slate-500">— {dsc}</span></span>
            </label>
          ))}
        </div>
        {preview && <ImportSummary p={preview} />}
        <div className="flex gap-2">
          {!preview ? (
            <button data-testid="st-import-check" disabled={!file || busy} onClick={() => send(false)} className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50">{busy ? "Reading…" : "Check file"}</button>
          ) : (
            <button data-testid="st-import-commit" disabled={busy || !preview.rows} onClick={() => send(true)} className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50">{busy ? "Importing…" : `Import ${preview.rows} student${preview.rows === 1 ? "" : "s"}`}</button>
          )}
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImportSummary({ p }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5" data-testid="st-import-summary">
      <div className="font-semibold text-slate-800" data-testid="st-import-rows">{p.rows} student row{p.rows === 1 ? "" : "s"} found → {p.tab_label}</div>
      {p.existing_matches > 0 && <div className="text-amber-700" data-testid="st-import-existing">{p.existing_matches} already in this tab — they'll be {p.mode === "override" ? "overwritten" : p.mode === "merge" ? "merged" : "added again as new rows"}</div>}
      {p.matched.length > 0 && <div><span className="text-slate-500">Matched: </span>{p.matched.map((m) => (m.header === m.column ? m.header : `${m.header} → ${m.column}`)).join(", ")}</div>}
      {p.new_columns.length > 0 && <div data-testid="st-import-new-cols"><span className="text-slate-500">New columns to add: </span>{p.new_columns.join(", ")}</div>}
      {p.ignored.length > 0 && <div className="text-slate-400">Ignored: {p.ignored.join(", ")}</div>}
      {p.sample[0] && <div className="text-slate-500">First row: {Object.values(p.sample[0]).slice(0, 5).join(" · ")}</div>}
    </div>
  );
}
